import type { TransactionInstruction } from '@solana/web3.js'
import { useWallet } from '@solana/wallet-adapter-vue'
import { ComputeBudgetProgram } from '@solana/web3.js'
import { defineStore } from 'pinia'
import { solToLamports } from '~/utils'

import {
  ADDITIONAL_COMPUTE_UNITS,
  BASE_PRIORITY_FEE,
  DEFAULT_COMPUTE_UNITS,
  DEFAULT_EXACT_FEE,
  MICRO_LAMPORTS_PER_LAMPORT,
  MULTIPLIER_COMPUTE_UNITS,
  PriorityLevel,
  PriorityMode,
} from './constants'
import { estimatePrioritizationFee, getSimulationComputeUnits } from './utils'

export { default as PriorityFee } from './components/PriorityFee.vue'

export const usePriorityFee = defineStore('priority-fee', () => {
  const mode = useLocalStorage<PriorityMode>('priorityMode', PriorityMode.MaxCap)
  const level = useLocalStorage<PriorityLevel>('priorityLevel', PriorityLevel.Turbo)
  const exactFee = useLocalStorage<number>('priorityFee', DEFAULT_EXACT_FEE)

  const baseFee = computed(() => {
    return BASE_PRIORITY_FEE[level.value] ?? 0
  })

  return {
    mode,
    level,
    exactFee,
    baseFee,
  }
})

export async function withPriorityFees({ computeUnits, instructions }: {
  computeUnits?: number
  instructions: TransactionInstruction[]
}): Promise<TransactionInstruction[]> {
  const connectionStore = useConnectionStore()
  const priorityFee = usePriorityFee()

  const wallet = useWallet()
  const { publicKey } = wallet

  let estimate: number

  let units = computeUnits ?? DEFAULT_COMPUTE_UNITS

  if (publicKey.value) {
    const unitsEstimated = await getSimulationComputeUnits(
      connectionStore.connection,
      instructions,
      publicKey.value,
      [],
    )
    if (unitsEstimated) {
      units = Math.ceil(unitsEstimated * MULTIPLIER_COMPUTE_UNITS) + ADDITIONAL_COMPUTE_UNITS
    }
    console.log('[priority] Estimated CU =', `${unitsEstimated} / ${units}`)
  }

  const exactFee = solToLamports(priorityFee.exactFee) * MICRO_LAMPORTS_PER_LAMPORT
  const cuPriceMax = Math.ceil(exactFee / units)

  if (priorityFee.mode === PriorityMode.ExactFee) {
    estimate = cuPriceMax
  } else {
    estimate = await estimatePrioritizationFee(connectionStore.connection, instructions, priorityFee.baseFee)
    console.log('[priority] Estimated CU price (mL):', estimate)
    estimate = Math.min(estimate, cuPriceMax)
  }

  console.log('[priority] Used CU price (mL):', estimate)
  console.log('[priority] Estimated prioritization fee:', estimate * units / MICRO_LAMPORTS_PER_LAMPORT)

  return [
    ComputeBudgetProgram.setComputeUnitLimit({ units }),
    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: estimate }),
    ...instructions,
  ]
}

export * from './constants'
